import { ActionNextStepPayload } from '../store/actions/types/form.actions';
import { FormState, InputAnswer } from '../store/reducers/types/form.types';
import {
  FormsLoadClientLogic,
  FormsLoadClientLogicRule,
  FormsLoadClientStep,
  FormsLoadClientStepOption,
  LogicRuleType,
  LogicThenType,
  LogicType,
  NextLogicRuleType,
} from './api/types/forms.responses.d';

export function evaluateRules(
  rules: FormsLoadClientLogicRule[],
  results: boolean[],
): boolean {
  if (rules.length !== results.length) {
    throw new Error(
      `rules (${rules.length}) and results (${results.length}) must be the same length`,
    );
  }
  if (results.length === 0) return true; // decide your default; could also be false
  if (results.length === 1) return results[0];

  let acc = results[0];

  for (let i = 0; i < results.length - 1; i++) {
    const logic = rules[i].nextRuleType ?? NextLogicRuleType.AND; // default if missing

    const next = results[i + 1];
    acc = logic === NextLogicRuleType.AND ? acc && next : acc || next;
  }

  return acc;
}

export function calculateNextStep(
  payload: ActionNextStepPayload,
  allSteps: FormsLoadClientStep[],
  logic: FormsLoadClientLogic[],
  answers: FormState['stepAnswers'],
  previousSteps: number[] = [],
): FormsLoadClientStep | null {
  const currentStep = allSteps.find(
    (step) => step.id === payload.current.stepId,
  );
  let optionsFound: FormsLoadClientStepOption[] | null = null;

  if (payload.current.optionId?.length) {
    currentStep?.inputs?.find((input) => {
      const _optionsFound = input.options?.filter((option) =>
        payload.current.optionId?.includes(option.id),
      );
      if (_optionsFound?.length) {
        optionsFound = _optionsFound.filter((option) =>
          payload.current.optionId?.includes(option.id),
        );
        if (optionsFound.length) {
          return true;
        }
      }
      return false;
    });
  }

  let nextStepId: number | null = null;

  // Find logic for the current step
  const stepLogic = logic.find((lg) => lg.stepId === currentStep?.id);
  if (stepLogic && answers) {
    // Evaluate rules
    const logicStep =
      allSteps.find((step) => step.id === stepLogic.stepId) || null;
    if (logicStep) {
      // Steps that have actually been visited: all previousSteps plus the
      // step the user is navigating away from right now.
      const visitedStepIds = new Set([
        ...previousSteps,
        payload.current.stepId,
      ]);

      const stepRules = stepLogic.rules.sort((a, b) => a.order - b.order);
      if (stepLogic.type === LogicType.IF) {
        const passes: boolean[] = stepRules.map((rule) => false);

        // Evaluate each rule
        stepRules.forEach((rule, index) => {
          // For IS_ANSWERED / IS_NOT_ANSWERED we need visitedStepIds, not
          // the per-option answer lookup used by IS / IS_NOT.
          if (
            rule.type === LogicRuleType.IS_ANSWERED ||
            rule.type === LogicRuleType.IS_NOT_ANSWERED
          ) {
            if (!rule.inputId) return;
            // Find which step owns this input.
            const ownerStep = allSteps.find((s) =>
              s.inputs?.some((i) => i.id === rule.inputId),
            );
            if (!ownerStep || !visitedStepIds.has(ownerStep.id)) {
              // Step was never visited — neither IS_ANSWERED nor IS_NOT_ANSWERED fires.
              passes[index] = false;
              return;
            }
            const stepAnswerData = answers?.[ownerStep.id];
            if (rule.type === LogicRuleType.IS_ANSWERED) {
              passes[index] = !!stepAnswerData?.answered;
            } else {
              // IS_NOT_ANSWERED: step was visited but not answered (skipped).
              passes[index] =
                stepAnswerData !== undefined && !stepAnswerData.answered;
            }
            return;
          }

          let answer: InputAnswer | undefined;

          Object.values(answers).find((stepAnswers) => {
            if (
              stepAnswers.answered &&
              rule.inputId &&
              stepAnswers.answers &&
              stepAnswers.answers[rule.inputId]
            ) {
              answer = stepAnswers.answers[rule.inputId];
              return true;
            }
            return false;
          });

          if (answer !== undefined) {
            if (rule.type === LogicRuleType.IS) {
              if (rule.option !== null) {
                if (answer.optionId?.includes(rule.option)) {
                  passes[index] = true;
                }
              } else if (rule.value !== null) {
                if (answer.value !== undefined && answer.value === rule.value) {
                  passes[index] = true;
                }
              }
            } else if (rule.type === LogicRuleType.IS_NOT) {
              if (rule.option !== null) {
                if (answer.optionId && !answer.optionId.includes(rule.option)) {
                  passes[index] = true;
                }
              } else if (rule.value !== null) {
                if (answer.value !== undefined && answer.value !== rule.value) {
                  passes[index] = true;
                }
              }
            }
          }
        });

        // Check if passes are correct
        const logicSatisfied = evaluateRules(stepRules, passes);
        if (
          logicSatisfied &&
          stepLogic.thenType === LogicThenType.GO_TO &&
          stepLogic.thenGoToStepId
        ) {
          nextStepId = stepLogic.thenGoToStepId;
        }
      }
    }
  }

  // If no logic, we try to use next step rules
  if (!nextStepId) {
    const firstOptionFound = (
      optionsFound as FormsLoadClientStepOption[] | null
    )?.[0];
    if (firstOptionFound?.nextStepId) {
      nextStepId = firstOptionFound.nextStepId;
    }
  }

  if (!nextStepId) {
    const stepNextStep = currentStep?.nextStepId || null;
    if (stepNextStep) {
      nextStepId = stepNextStep;
    }
  }

  // Return next step if found
  if (nextStepId !== null) {
    return allSteps.find((step) => step.id === nextStepId) || null;
  }

  return null;
}
